import { reactive } from "@vue/composition-api";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import { AcSellContractResult } from "@/components/Autocomplete/ac-sell-contract";
import { IlsctlLineItem } from "@/models/IlsctlLineItem";
import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientAutocomplete } from "@/models/Client";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";
import { WeightQuantityPriceStatus } from "@/models/WeightQuantityPriceStatus";

export const enum SellContractStatus {
  incomplete = "incomplete",
  completed = "completed",
  archived = "archived",
}

const prettifiedSellContractStatus = {
  incomplete: "Incomplete",
  completed: "Completed",
  archived: "Archived",
};

export const prettifySellContractStatus = (s: SellContractStatus) => {
  return prettifiedSellContractStatus[s];
};

export const sellContractStatusReverseLookup = <
  { [key: string]: SellContractStatus }
>{
  incomplete: SellContractStatus.incomplete,
  completed: SellContractStatus.completed,
  archived: SellContractStatus.archived,
};

export interface SellContractApiParams {
  average_weight_lot_unit_id: string;
  client_id: string;
  commodity_id: string;
  description: string;
  destination: string;
  end_date: string;
  end_quantity: number;
  end_weight: number;
  id: string;
  lot_unit_id: string;
  quantity_status: WeightQuantityPriceStatus;
  sell_price: number;
  sell_price_status: WeightQuantityPriceStatus;
  start_date: string;
  status: SellContractStatus;
  weight_lot_unit_id: string;
  weight_sell_price: number;
  weight_sell_price_status: WeightQuantityPriceStatus;
  weight_status: WeightQuantityPriceStatus;
}

export const SellContractNumberKeys: Array<keyof SellContract> = [
  "sell_price",
  "weight_sell_price",
  "end_quantity",
  "end_weight",
  "ratio_sold",
  "percent_sold",
  "average_weight",
  "remaining_quantity",
  "remaining_weight",
  "sold_weight",
  "sold_quantity",
];

export interface SellContract {
  average_weight_lot_unit_id: string;
  client_id: string;
  commodity_id: string;
  description: string;
  destination: string;
  end_date: string;
  end_quantity: number;
  end_weight: number;
  id: string;
  label: string;
  lot_unit_id: string;
  quantity_status: WeightQuantityPriceStatus;
  name: string;
  sell_price: number;
  sell_price_status: WeightQuantityPriceStatus;
  start_date: string;
  status: SellContractStatus;
  weight_lot_unit_id: string;
  weight_sell_price: number;
  weight_sell_price_status: WeightQuantityPriceStatus;
  weight_status: WeightQuantityPriceStatus;

  // These are the items that are added
  // The db table does not store them
  average_weight: number;
  average_weight_lot_unit_label: string;
  client_label: string;
  commodity_color: string;
  commodity_label: string;
  lot_unit_label: string;
  ratio_sold: number;
  percent_sold: number;
  remaining_quantity: number;
  remaining_weight: number;
  sold_quantity: number;
  sold_weight: number;
  weight_lot_unit_label: string;
  ilsctl_line_items: IlsctlLineItem[];
}

export interface FormSellContract extends SellContractApiParams {
  formCommodity: AutocompleteResult;
  formClient: AutocompleteResult;
  formLotUnit: AutocompleteResult;
  formWeightLotUnit: AutocompleteResult;
  formAverageWeightLotUnit: AutocompleteResult;
}

interface hasSellContractId {
  sellContractId: string;
}

export function buildNewSellContract(): SellContract {
  return {
    average_weight_lot_unit_id: "",
    client_id: "",
    commodity_id: "",
    description: "",
    destination: "",
    end_date: "",
    end_quantity: 0,
    end_weight: 0,
    id: "",
    label: "",
    lot_unit_id: "",
    quantity_status: WeightQuantityPriceStatus.estimated,
    name: "",
    sell_price: 0,
    sell_price_status: WeightQuantityPriceStatus.estimated,
    start_date: "",
    status: SellContractStatus.incomplete,
    weight_lot_unit_id: "",
    weight_sell_price: 0,
    weight_sell_price_status: WeightQuantityPriceStatus.estimated,
    weight_status: WeightQuantityPriceStatus.estimated,

    average_weight: 0,
    average_weight_lot_unit_label: "",
    client_label: "",
    commodity_color: "",
    commodity_label: "",
    lot_unit_label: "",
    ratio_sold: 0,
    percent_sold: 0,
    remaining_quantity: 0,
    remaining_weight: 0,
    sold_quantity: 0,
    sold_weight: 0,
    weight_lot_unit_label: "",
    ilsctl_line_items: [],
  };
}

export function buildFormSellContract() {
  let { formCommodity } = buildFormCommodityAutocomplete();
  let { formClient } = buildFormClientAutocomplete();
  let { formLotUnit } = buildFormLotUnitAutocomplete();

  let formSellContract = reactive<FormSellContract>({
    average_weight_lot_unit_id: "",
    client_id: "",
    commodity_id: "",
    description: "",
    destination: "",
    end_date: "",
    end_quantity: 0,
    end_weight: 0,
    id: "",
    lot_unit_id: "",
    quantity_status: WeightQuantityPriceStatus.estimated,
    sell_price: 0,
    sell_price_status: WeightQuantityPriceStatus.estimated,
    start_date: "",
    status: SellContractStatus.incomplete,
    weight_lot_unit_id: "",
    weight_sell_price: 0,
    weight_sell_price_status: WeightQuantityPriceStatus.estimated,
    weight_status: WeightQuantityPriceStatus.estimated,

    formCommodity: formCommodity,
    formClient: formClient,
    formLotUnit: formLotUnit(),
    formWeightLotUnit: formLotUnit(),
    formAverageWeightLotUnit: formLotUnit(),
  });

  let loadSellContractIntoForm = (sc: SellContract, fsc: FormSellContract) => {
    fsc.average_weight_lot_unit_id = sc.average_weight_lot_unit_id;
    fsc.client_id = sc.client_id;
    fsc.commodity_id = sc.commodity_id;
    fsc.description = sc.description;
    fsc.destination = sc.destination;
    fsc.end_date = sc.end_date;
    fsc.end_quantity = sc.end_quantity;
    fsc.end_weight = sc.end_weight;
    fsc.id = sc.id;
    fsc.lot_unit_id = sc.lot_unit_id;
    fsc.quantity_status = sc.quantity_status;
    fsc.sell_price_status = sc.sell_price_status;
    fsc.sell_price = sc.sell_price;
    fsc.start_date = sc.start_date;
    fsc.status = sc.status;
    fsc.weight_lot_unit_id = sc.weight_lot_unit_id;
    fsc.weight_sell_price = sc.weight_sell_price;
    fsc.weight_sell_price_status = sc.weight_sell_price_status;
    fsc.weight_status = sc.weight_status;

    fsc.formCommodity.label = sc.commodity_label;
    fsc.formCommodity.id = sc.commodity_id;
    fsc.formClient.label = sc.client_label;
    fsc.formClient.id = sc.client_id;
    fsc.formLotUnit.label = sc.lot_unit_label;
    fsc.formLotUnit.id = sc.lot_unit_id;
    fsc.formWeightLotUnit.label = sc.weight_lot_unit_label;
    fsc.formWeightLotUnit.id = sc.weight_lot_unit_id;
    fsc.formAverageWeightLotUnit.label = sc.average_weight_lot_unit_label;
    fsc.formAverageWeightLotUnit.id = sc.average_weight_lot_unit_id;
  };

  let sellContractStatuses = reactive<SellContractStatus[]>([]);

  const loadSellContractStatusesIntoForm = (
    scStatuses: SellContractStatus[],
  ) => {
    scStatuses.push(
      SellContractStatus.incomplete,
      SellContractStatus.completed,
      SellContractStatus.archived,
    );
  };

  return {
    formSellContract,
    loadSellContractIntoForm,
    sellContractStatuses,
    loadSellContractStatusesIntoForm,
  };
}

export function buildFormSellContractAutocomplete() {
  let formSellContract = () => {
    return reactive<AcSellContractResult>({
      id: "",
      label: "",
      clientLabel: "",
      commodityLabel: "",
      sellPrice: 0,
      lotUnitLabel: "",
      lotUnitId: "",
    });
  };

  let updateSellContract = (
    evt: AcSellContractResult,
    formSellContract: AcSellContractResult,
    formData: hasSellContractId,
  ) => {
    formSellContract.id = evt.id;
    formSellContract.label = evt.label;
    formSellContract.clientLabel = evt.clientLabel;
    formSellContract.commodityLabel = evt.commodityLabel;
    formSellContract.sellPrice = evt.sellPrice;
    formSellContract.lotUnitLabel = evt.lotUnitLabel;
    formSellContract.lotUnitId = evt.lotUnitId;
    formData.sellContractId = evt.id;
  };

  return {
    formSellContract,
    updateSellContract,
  };
}
